import { appConfirm } from "@/lib/appDialog";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { publicFileUrl } from "@/lib/signedFile";
import { Loader2, Search, Sparkles, Save, X, RefreshCw, Trophy, Upload, Plus, Trash2, Image as ImageIcon, ArrowRight, Eye, AlertCircle } from "lucide-react";

const CAP_FIELDS = [
  { key: "students", label: "الطلاب المسجّلون" },
  { key: "certificates", label: "الشهادات الموثّقة" },
  { key: "associations", label: "الجمعيات الشريكة" },
  { key: "partners", label: "شركاء النجاح" },
  { key: "opportunities", label: "الفرص المنشورة" },
  { key: "placements", label: "تسكين الطلاب" },
  { key: "tasks", label: "إجمالي المهام" },
  { key: "completed_hours", label: "الساعات المنجزة" },
  { key: "sessions", label: "إجمالي الجلسات" },
];

const OVR_FIELDS = [
  { key: "program_label", label: "اسم البرنامج / الفرصة", type: "text", placeholder: "يُسحب تلقائيًا من التسكين" },
  { key: "hours", label: "الساعات المُقدَّمة", type: "number" },
  { key: "tasks_done", label: "المهام المكتملة", type: "number" },
  { key: "tasks_total", label: "إجمالي المهام", type: "number" },
  { key: "eval_pct", label: "متوسط تقييم الأداء %", type: "number" },
  { key: "attendance_pct", label: "متوسط الحضور %", type: "number" },
  { key: "progress_pct", label: "تقدّم الأداء %", type: "number" },
  { key: "association_label", label: "اسم الجمعية / المنظمة", type: "text", placeholder: "تُسحب تلقائيًا من التسكين" },
  { key: "since_months", label: "عدد الأشهر", type: "number" },
];

export default function LandingAdmin() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingExit, setSavingExit] = useState(false);
  const [settings, setSettings] = useState(null);
  const [topStudent, setTopStudent] = useState(null);
  const [publicPreview, setPublicPreview] = useState(null);
  const [q, setQ] = useState("");
  const [candidates, setCandidates] = useState([]);
  const [searching, setSearching] = useState(false);
  const [partners, setPartners] = useState([]);
  const [partnerName, setPartnerName] = useState("");
  const [partnerLogoUrl, setPartnerLogoUrl] = useState("");
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [addingPartner, setAddingPartner] = useState(false);
  const [bulkPartners, setBulkPartners] = useState([]);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ done: 0, total: 0 });
  const [bulkInputKey, setBulkInputKey] = useState(0);
  const baselineRef = useRef(null);

  // Dirty flag: any diff between current settings and last saved baseline
  const dirty = useMemo(() => {
    if (!settings || !baselineRef.current) return false;
    try {
      const norm = (o) => JSON.stringify({
        top_student_id: o.top_student_id ?? null,
        top_student_override: o.top_student_override || {},
        caps: o.caps || {},
      });
      return norm(settings) !== norm(baselineRef.current);
    } catch { return false; }
  }, [settings]);

  const load = async () => {
    setLoading(true);
    try {
      const [s, p] = await Promise.all([
        api.get("/admin/landing-settings"),
        api.get("/public/landing"),
      ]);
      setSettings(s.data.settings);
      baselineRef.current = JSON.parse(JSON.stringify(s.data.settings));
      setTopStudent(s.data.top_student);
      setPublicPreview(p.data);
      setPartners(s.data.settings?.partners_list || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const searchCandidates = async (initial) => {
    setSearching(true);
    try {
      const { data } = await api.get(`/admin/landing/top-candidates${initial ? "" : `?q=${encodeURIComponent(q)}`}`);
      setCandidates(data.candidates || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSearching(false);
    }
  };

  useEffect(() => { if (!loading) searchCandidates(true); /* eslint-disable-next-line */ }, [loading]);

  const updateCap = (k, v) => setSettings((s) => ({ ...s, caps: { ...s.caps, [k]: v } }));
  const updateOverride = (k, v) => setSettings((s) => ({ ...s, top_student_override: { ...(s.top_student_override || {}), [k]: v } }));

  const save = async ({ exit = false } = {}) => {
    if (exit) setSavingExit(true); else setSaving(true);
    try {
      const payload = {
        top_student_id: settings?.top_student_id ?? null,
        top_student_override: settings?.top_student_override || {},
        caps: Object.fromEntries(
          Object.entries(settings?.caps || {}).map(([k, v]) => [k, Number(v) || 100])
        ),
      };
      const { data } = await api.patch("/admin/landing-settings", payload);
      setSettings(data.settings);
      setTopStudent(data.top_student);
      toast.success("تم حفظ الإعدادات وتحديث الصفحة الرئيسية");
      if (exit) {
        navigate("/dashboard/landing/preview");
        return;
      }
      const p = await api.get("/public/landing");
      setPublicPreview(p.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
      setSavingExit(false);
    }
  };

  const pickStudent = (cand) => {
    setSettings((s) => ({ ...s, top_student_id: cand.id }));
    toast.info(`تم اختيار ${cand.name} — اضغط "حفظ" أو "حفظ ومعاينة" للتفعيل`);
  };

  const clearStudent = () => setSettings((s) => ({ ...s, top_student_id: null }));

  const uploadLogo = async (file) => {
    if (!file) return;
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("context", "partner_logo");
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPartnerLogoUrl(data.file_id || "");
      toast.success("تم رفع الشعار");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploadingLogo(false);
    }
  };

  const addPartner = async () => {
    const name = partnerName.trim();
    const logo = partnerLogoUrl.trim();
    if (!name && !logo) { toast.error("أضف اسم الشريك أو شعاره على الأقل"); return; }
    setAddingPartner(true);
    try {
      const { data } = await api.post("/admin/landing/partners", {
        name: name || null,
        logo_url: logo || null,
      });
      setPartners(data.partners_list || []);
      setPartnerName("");
      setPartnerLogoUrl("");
      toast.success("أُضيف الشريك — يظهر في الشريط مباشرة");
      const p = await api.get("/public/landing");
      setPublicPreview(p.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setAddingPartner(false);
    }
  };

  const selectBulkLogos = (files) => {
    const selected = Array.from(files || []).slice(0, 50).map((file) => ({
      file,
      previewUrl: URL.createObjectURL(file),
      name: file.name.replace(/\.[^.]+$/, "").replace(/[_-]+/g, " ").trim(),
    }));
    setBulkPartners((items) => {
      items.forEach((item) => URL.revokeObjectURL(item.previewUrl));
      return selected;
    });
    setBulkProgress({ done: 0, total: selected.length });
    if ((files?.length || 0) > 50) toast.info("يمكن إضافة 50 شعارًا في الدفعة الواحدة");
  };

  const updateBulkPartnerName = (index, name) => setBulkPartners((items) => items.map((item, i) => i === index ? { ...item, name } : item));
  const removeBulkPartner = (index) => setBulkPartners((items) => {
    URL.revokeObjectURL(items[index]?.previewUrl);
    return items.filter((_, i) => i !== index);
  });

  const addPartnersBulk = async () => {
    if (!bulkPartners.length) return toast.error("اختر شعارات الشركاء أولًا");
    setBulkUploading(true);
    setBulkProgress({ done: 0, total: bulkPartners.length });
    try {
      const uploaded = [];
      for (let index = 0; index < bulkPartners.length; index += 1) {
        const item = bulkPartners[index];
        const fd = new FormData();
        fd.append("file", item.file);
        fd.append("context", "partner_logo");
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push({ name: item.name.trim() || null, logo_url: data.file_id });
        setBulkProgress({ done: index + 1, total: bulkPartners.length });
      }
      const { data } = await api.post("/admin/landing/partners/bulk", { items: uploaded });
      setPartners(data.partners_list || []);
      setBulkPartners((items) => {
        items.forEach((item) => URL.revokeObjectURL(item.previewUrl));
        return [];
      });
      setBulkInputKey((value) => value + 1);
      toast.success(`تمت إضافة ${data.added?.length || uploaded.length} شعارات شركاء`);
      const preview = await api.get("/public/landing");
      setPublicPreview(preview.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBulkUploading(false);
    }
  };

  const removePartner = async (id) => {
    if (!await appConfirm("حذف هذا الشريك من الشريط؟")) return;
    try {
      const { data } = await api.delete(`/admin/landing/partners/${id}`);
      setPartners(data.partners_list || []);
      toast.success("تم حذف الشريك");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (loading) {
    return <div className="grid place-items-center py-24"><Loader2 className="h-8 w-8 animate-spin text-teal-700" /></div>;
  }

  return (
    <div data-testid="landing-admin" className="max-w-6xl mx-auto">
      {/* Breadcrumb + quick actions */}
      <div className="mb-4 flex items-center justify-between flex-wrap gap-2">
        <button
          onClick={() => navigate("/dashboard")}
          className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-teal-700 font-medium"
          data-testid="landing-admin-back"
        >
          <ArrowRight className="h-4 w-4" />
          العودة إلى لوحة التحكم
        </button>
        <button
          onClick={() => navigate("/dashboard/landing/preview")}
          className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-teal-700"
          data-testid="landing-admin-view-site"
        >
          معاينة الصفحة الرئيسية ↗
        </button>
      </div>

      <div className="mb-8">
        <div className="text-xs font-bold uppercase tracking-widest text-teal-700">إعدادات الصفحة الرئيسية</div>
        <h1 className="mt-2 text-2xl font-heading font-black text-slate-900 flex items-center gap-2">
          <Sparkles className="h-6 w-6 text-amber-500" />
          الطالب المتفوّق وأرقام الأثر
        </h1>
        <p className="mt-2 text-slate-600 text-sm">
          اختر الطالب المتفوّق ليظهر بنفس قالب صفحة الأثر، وحدّد الحد الأقصى لكل رقم على الصفحة الرئيسية (بعده يظهر "+الحد").
        </p>
        {dirty && (
          <div
            className="mt-4 flex items-start gap-2 px-4 py-2.5 rounded-lg bg-amber-50 border border-amber-200 text-amber-900 text-sm"
            role="status"
            data-testid="unsaved-badge"
          >
            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
            <div>
              <span className="font-semibold">لديك تغييرات غير محفوظة.</span>
              <span className="ms-1">اضغط "حفظ ومعاينة" لتفعيلها على الصفحة الرئيسية.</span>
            </div>
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Live preview */}
        <div className="p-4 rounded-2xl bg-white border border-cream-200 card-shadow" data-testid="landing-live-preview">
          <div className="flex items-center justify-between mb-4">
            <div className="font-heading font-bold text-slate-900 flex items-center gap-2">
              <Trophy className="h-4 w-4 text-amber-500" /> المعاينة المباشرة
            </div>
            <button onClick={load} className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-teal-700">
              <RefreshCw className="h-3 w-3" /> تحديث
            </button>
          </div>
          <div className="grid grid-cols-2 gap-3 mb-4" data-testid="preview-stats">
            {CAP_FIELDS.map(({ key, label }) => {
              const s = publicPreview?.stats?.[key];
              const raw = publicPreview?.raw?.[key] ?? 0;
              return (
                <div key={key} className="p-3 rounded-xl bg-cream-50 border border-cream-200">
                  <div className="text-xs text-slate-500">{label}</div>
                  <div className="mt-1 text-2xl font-heading font-black text-teal-700 arabic-numerals">
                    {s?.display ?? "—"}
                  </div>
                  <div className="text-[10px] text-slate-400 mt-0.5 arabic-numerals">
                    الفعلي: {raw} {s?.capped ? "· مُحدَّد بالسقف" : ""}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="p-3 rounded-xl bg-cream-50 border border-cream-200">
            <div className="text-xs text-slate-500 mb-1">بطاقة الطالب المتفوّق</div>
            {topStudent ? (
              <div className="text-sm">
                <div className="font-semibold text-slate-900">{topStudent.name}</div>
                <div className="text-xs text-slate-600 mt-1 arabic-numerals">
                  {topStudent.hours}س · {topStudent.tasks_done}/{topStudent.tasks_total} مهام · {topStudent.eval_pct}% تقييم · {topStudent.progress_pct}% تقدّم
                </div>
              </div>
            ) : (
              <div className="text-xs text-slate-500">لم يتم اختيار طالب متفوّق — القالب الافتراضي يظهر على الصفحة</div>
            )}
          </div>
        </div>

        {/* Caps */}
        <div className="p-4 rounded-2xl bg-white border border-cream-200 card-shadow" data-testid="landing-caps">
          <div className="font-heading font-bold text-slate-900 mb-1">الحد الأقصى للأرقام</div>
          <div className="text-xs text-slate-500 mb-4">إذا تجاوز الرقم الفعلي هذا الحد، يظهر بصيغة "+الحد" (مثال: +100)</div>
          <div className="grid sm:grid-cols-2 gap-3">
            {CAP_FIELDS.map(({ key, label }) => (
              <label key={key} className="block">
                <span className="text-xs text-slate-600">{label}</span>
                <input
                  type="number"
                  min={1}
                  max={100000}
                  value={settings?.caps?.[key] ?? 100}
                  onChange={(e) => updateCap(key, e.target.value)}
                  className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none arabic-numerals"
                  data-testid={`cap-${key}`}
                />
              </label>
            ))}
          </div>
        </div>
      </div>

      {/* Student picker */}
      <div className="mt-6 p-4 rounded-2xl bg-white border border-cream-200 card-shadow" data-testid="landing-student-picker">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div>
            <div className="font-heading font-bold text-slate-900">اختر الطالب المتفوّق</div>
            <div className="text-xs text-slate-500 mt-0.5">
              المختار حالياً: <span className="font-semibold text-teal-700">{topStudent?.name || "— لا شيء —"}</span>
              {settings?.top_student_id && (
                <button
                  onClick={clearStudent}
                  className="inline-flex items-center gap-1 ms-2 text-xs text-red-600 hover:underline"
                  data-testid="clear-top-student"
                ><X className="h-3 w-3" /> إلغاء الاختيار</button>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="h-4 w-4 text-slate-400 absolute top-1/2 -translate-y-1/2 start-3" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") searchCandidates(false); }}
                placeholder="ابحث بالاسم / الجامعة / التخصص"
                className="ps-9 pe-3 py-2 rounded-lg border border-slate-300 bg-white text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none w-64"
                data-testid="candidate-search-input"
              />
            </div>
            <button
              onClick={() => searchCandidates(false)}
              className="px-3 py-2 rounded-lg bg-teal-700 text-white text-sm font-semibold hover:bg-teal-600 disabled:opacity-60"
              disabled={searching}
              data-testid="candidate-search-btn"
            >
              {searching ? "…" : "بحث"}
            </button>
          </div>
        </div>
        <div className="max-h-80 overflow-y-auto rounded-lg border border-cream-200">
          <table className="w-full text-sm">
            <thead className="bg-cream-50 text-xs text-slate-600">
              <tr>
                <th className="text-start px-3 py-2">الاسم</th>
                <th className="text-start px-3 py-2">الجامعة</th>
                <th className="text-start px-3 py-2">التخصص</th>
                <th className="text-start px-3 py-2">المدينة</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody data-testid="candidates-list">
              {candidates.map((c) => {
                const isPicked = settings?.top_student_id === c.id;
                return (
                  <tr key={c.id} className={`border-t border-cream-100 ${isPicked ? "bg-teal-50/50" : ""}`}>
                    <td className="px-3 py-2 font-semibold text-slate-900">
                      {c.name}
                      {c.is_graduate && <span className="text-[10px] text-slate-500 ms-1">🎓</span>}
                    </td>
                    <td className="px-3 py-2 text-slate-600">{c.university || "—"}</td>
                    <td className="px-3 py-2 text-slate-600">{c.major || "—"}</td>
                    <td className="px-3 py-2 text-slate-600">{c.city || "—"}</td>
                    <td className="px-3 py-2 text-end">
                      <button
                        onClick={() => pickStudent(c)}
                        className={`px-3 py-1.5 rounded-md text-xs font-semibold ${
                          isPicked
                            ? "bg-emerald-100 text-emerald-800 border border-emerald-200"
                            : "bg-teal-700 text-white hover:bg-teal-600"
                        }`}
                        data-testid={`pick-student-${c.id}`}
                      >
                        {isPicked ? "مختار حالياً" : "اختيار"}
                      </button>
                    </td>
                  </tr>
                );
              })}
              {candidates.length === 0 && (
                <tr><td colSpan={5} className="px-3 py-6 text-center text-xs text-slate-500">لا يوجد طلاب مطابقون</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Overrides for card */}
      <div className="mt-6 p-4 rounded-2xl bg-white border border-cream-200 card-shadow" data-testid="landing-overrides">
        <div className="font-heading font-bold text-slate-900 mb-1">تفاصيل بطاقة الطالب (اختياري)</div>
        <div className="text-xs text-slate-500 mb-4">
          خصّص القيم التي تظهر في بطاقة الطالب المتفوّق على الصفحة الرئيسية. اترك أي حقل فارغاً للقيمة الافتراضية.
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {OVR_FIELDS.map(({ key, label, type, placeholder }) => (
            <label key={key} className="block">
              <span className="text-xs text-slate-600">{label}</span>
              <input
                type={type}
                value={settings?.top_student_override?.[key] ?? ""}
                onChange={(e) => updateOverride(key, type === "number" ? (e.target.value === "" ? null : Number(e.target.value)) : e.target.value)}
                placeholder={placeholder || ""}
                className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none"
                data-testid={`override-${key}`}
              />
            </label>
          ))}
        </div>
      </div>

      {/* Save bar */}
      <div className="sticky bottom-4 mt-8 flex flex-wrap justify-end gap-2">
        <button
          onClick={() => navigate("/dashboard")}
          disabled={saving || savingExit}
          className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white border border-slate-300 text-slate-700 font-semibold hover:bg-cream-50 disabled:opacity-60 shadow-sm"
          data-testid="cancel-landing-settings"
        >
          <X className="h-4 w-4" /> إلغاء
        </button>
        <button
          onClick={() => save({ exit: false })}
          disabled={saving || savingExit}
          className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white border-2 border-teal-700 text-teal-800 font-semibold hover:bg-teal-50 disabled:opacity-60 shadow-md"
          data-testid="save-landing-settings"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          حفظ التغييرات والبقاء
        </button>
        <button
          onClick={() => save({ exit: true })}
          disabled={saving || savingExit}
          className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-teal-700 text-white font-semibold hover:bg-teal-600 disabled:opacity-60 shadow-lg"
          data-testid="save-exit-landing-settings"
        >
          {savingExit ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}
          حفظ ومعاينة الصفحة الرئيسية
        </button>
      </div>

      <AmbassadorsManager initial={publicPreview?.ambassadors_list || settings?.ambassadors_list || []} onChanged={async () => { const p = await api.get("/public/landing"); setPublicPreview(p.data); }} />

      {/* Partners strip management */}
      <div className="mt-8 p-4 rounded-2xl bg-white border border-cream-200 card-shadow" data-testid="landing-partners">
        <div className="font-heading font-bold text-slate-900 mb-1">شريط شركاء النجاح</div>
        <div className="text-xs text-slate-500 mb-4">
          الشركاء المضافون هنا يظهرون فوراً في شريط "شركاء الأثر مع عماد" على الصفحة الرئيسية. عند إفراغ القائمة يعود العرض الافتراضي.
        </div>

        <div className="mb-5 rounded-xl border border-teal-200 bg-teal-50/50 p-4" data-testid="partners-bulk-upload">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div><div className="text-sm font-bold text-slate-900">إضافة شعارات بالجملة</div><div className="mt-1 text-xs text-slate-500">اختر حتى 50 صورة، ثم راجع أسماء الجهات قبل الإضافة.</div></div>
            <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-teal-300 bg-white px-4 py-2 text-sm font-semibold text-teal-800 hover:bg-teal-50">
              <Upload className="h-4 w-4" /> اختيار عدة شعارات
              <input key={bulkInputKey} type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden" onChange={(e) => selectBulkLogos(e.target.files)} data-testid="partners-bulk-files" />
            </label>
          </div>
          {bulkPartners.length > 0 && <div className="mt-4 space-y-2">
            {bulkPartners.map((item, index) => <div key={`${item.file.name}-${item.file.lastModified}-${index}`} className="grid gap-2 rounded-lg border bg-white p-2 sm:grid-cols-[2.5rem_1fr_auto] sm:items-center">
              <div className="h-10 w-10 overflow-hidden rounded-lg bg-slate-100"><img src={item.previewUrl} alt="" className="h-full w-full object-contain" /></div>
              <div><input value={item.name} onChange={(e) => updateBulkPartnerName(index, e.target.value)} placeholder="اسم الجهة (اختياري)" className="w-full rounded-lg border px-3 py-2 text-sm" /><div className="mt-1 truncate text-[10px] text-slate-400">{item.file.name}</div></div>
              <button type="button" onClick={() => removeBulkPartner(index)} disabled={bulkUploading} className="rounded-lg p-2 text-red-600 hover:bg-red-50" aria-label={`إزالة ${item.file.name}`}><X className="h-4 w-4" /></button>
            </div>)}
            <div className="flex flex-wrap items-center justify-between gap-3 pt-2"><span className="text-xs text-slate-600">{bulkUploading ? `جاري الرفع ${bulkProgress.done} من ${bulkProgress.total}` : `تم اختيار ${bulkPartners.length} شعار`}</span><button onClick={addPartnersBulk} disabled={bulkUploading} className="inline-flex items-center gap-2 rounded-lg bg-teal-700 px-5 py-2.5 text-sm font-bold text-white disabled:opacity-60">{bulkUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} رفع وإضافة الجميع</button></div>
          </div>}
        </div>

        <div className="grid sm:grid-cols-[1fr_1fr_auto_auto] gap-3 items-end mb-5 p-4 rounded-xl bg-cream-50 border border-cream-200">
          <label className="block">
            <span className="text-xs text-slate-600">اسم الجهة (اختياري — إذا كان الشعار يتضمّن الاسم)</span>
            <input
              value={partnerName}
              onChange={(e) => setPartnerName(e.target.value)}
              placeholder="مثال: جمعية بنيان — اتركه فارغاً إن كان الشعار مكتفياً بنفسه"
              className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none"
              data-testid="partner-name-input"
            />
          </label>
          <label className="block">
            <span className="text-xs text-slate-600">رابط الشعار (اختياري)</span>
            <input
              value={partnerLogoUrl}
              onChange={(e) => setPartnerLogoUrl(e.target.value)}
              placeholder="https://... أو ارفع أدناه"
              className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none"
              data-testid="partner-logo-url-input"
            />
          </label>
          <label className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white border border-slate-300 text-sm font-semibold text-slate-700 cursor-pointer hover:bg-cream-50" data-testid="partner-logo-upload">
            {uploadingLogo ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            رفع شعار
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={(e) => uploadLogo(e.target.files?.[0])}
            />
          </label>
          <button
            onClick={addPartner}
            disabled={addingPartner}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-teal-700 text-white text-sm font-semibold hover:bg-teal-600 disabled:opacity-60"
            data-testid="partner-add-btn"
          >
            {addingPartner ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            إضافة
          </button>
          {partnerLogoUrl && (
            <div className="sm:col-span-4 flex items-center gap-2 text-xs text-slate-600">
              <div className="h-10 w-10 rounded-lg bg-slate-100 overflow-hidden grid place-items-center">
                <img src={publicFileUrl(partnerLogoUrl)} alt="preview" className="h-full w-full object-cover" />
              </div>
              معاينة الشعار
              <button onClick={() => setPartnerLogoUrl("")} className="ms-2 text-red-600 hover:underline inline-flex items-center gap-1"><X className="h-3 w-3" /> إزالة</button>
            </div>
          )}
        </div>

        {partners.length === 0 ? (
          <div className="p-4 rounded-lg bg-cream-50 border border-dashed border-cream-300 text-center text-xs text-slate-500 flex flex-col items-center gap-2">
            <ImageIcon className="h-6 w-6 text-slate-400" />
            لا يوجد شركاء مضافون. حالياً يظهر العرض الافتراضي على الصفحة.
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="partners-list">
            {partners.map((p) => (
              <div key={p.id} className="flex items-center gap-3 p-3 rounded-xl bg-cream-50 border border-cream-200">
                <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-teal-700 to-teal-900 text-white grid place-items-center font-heading font-black overflow-hidden shrink-0">
                  {p.logo_url
                    ? <img src={publicFileUrl(p.logo_url)} alt="" className="h-full w-full object-cover" />
                    : (p.name?.[0] || "?")}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-slate-900 truncate text-sm">
                    {p.name || <span className="italic text-slate-500">— شعار بلا اسم —</span>}
                  </div>
                  <div className="text-[10px] text-slate-500">شريك نجاح</div>
                </div>
                <button
                  onClick={() => removePartner(p.id)}
                  className="p-2 rounded-lg text-red-600 hover:bg-red-50"
                  aria-label={`حذف ${p.name || "الشريك"}`}
                  data-testid={`partner-delete-${p.id}`}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function AmbassadorsManager({ initial, onChanged }) {
  const initialItems = Array.isArray(initial) ? initial : [];
  const [items, setItems] = useState(initialItems), [saving, setSaving] = useState(false);
  useEffect(() => { setItems(Array.isArray(initial) ? initial : []); }, [initial]);
  const [form, setForm] = useState({ name: "", photo_url: "", affiliation: "", code: "", quote: "" });
  const update=(key,value)=>setForm(f=>({...f,[key]:value}));
  const add=async()=>{if(!form.name.trim())return toast.error("اسم السفير مطلوب");setSaving(true);try{const{data}=await api.post("/admin/landing/ambassadors",{...form});setItems(data.ambassadors_list||[]);setForm({name:"",photo_url:"",affiliation:"",code:"",quote:""});toast.success("أُضيف السفير إلى الصفحة الرئيسية");await onChanged?.();}catch(e){toast.error(formatApiError(e));}finally{setSaving(false);}};
  const remove=async(id)=>{if(!await appConfirm("حذف هذا السفير من الصفحة الرئيسية؟"))return;try{const{data}=await api.delete(`/admin/landing/ambassadors/${id}`);setItems(data.ambassadors_list||[]);toast.success("تم حذف السفير");await onChanged?.();}catch(e){toast.error(formatApiError(e));}};
  return <section className="mt-8 rounded-2xl bg-white p-5 shadow-sm" data-testid="landing-ambassadors"><div className="flex items-start gap-3"><div className="grid h-10 w-10 place-items-center rounded-xl bg-amber-100 text-amber-800"><Sparkles className="h-5 w-5"/></div><div><h2 className="font-heading text-lg font-black text-slate-900">سفراء عماد</h2><p className="mt-1 text-sm text-slate-500">أضف السفراء الذين يظهرون في قسم برنامج السفراء على الصفحة الرئيسية.</p></div></div>
    <div className="mt-5 grid gap-3 md:grid-cols-2"><Field label="اسم السفير" value={form.name} onChange={v=>update("name",v)} testid="ambassador-name-input" required/><Field label="الجامعة أو الجهة" value={form.affiliation} onChange={v=>update("affiliation",v)} testid="ambassador-affiliation-input"/><Field label="رابط الصورة" value={form.photo_url} onChange={v=>update("photo_url",v)} testid="ambassador-photo-input"/><Field label="رمز الإحالة" value={form.code} onChange={v=>update("code",v)} testid="ambassador-code-input"/><label className="md:col-span-2 text-sm font-medium text-slate-700">اقتباس السفير<textarea rows={3} value={form.quote} onChange={e=>update("quote",e.target.value)} className="mt-2 w-full rounded-xl border border-slate-300 p-3 text-base"/></label><button onClick={add} disabled={saving} className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-teal-700 px-5 font-bold text-white disabled:opacity-60 md:col-span-2">{saving?<Loader2 className="h-4 w-4 animate-spin"/>:<Plus className="h-4 w-4"/>}إضافة السفير</button></div>
    <div className="mt-5 grid gap-3 sm:grid-cols-2">{items.map(item=><article key={item.id} className="flex items-center gap-3 rounded-xl bg-slate-50 p-3">{item.photo_url?<img src={item.photo_url} alt="" className="h-12 w-12 rounded-full object-cover"/>:<div className="grid h-12 w-12 place-items-center rounded-full bg-teal-100 font-black text-teal-800">{item.name?.[0]}</div>}<div className="min-w-0 flex-1"><b className="block truncate">{item.name}</b><span className="text-xs text-slate-500">{item.affiliation||"سفير عماد"} · {item.students_count||0} إحالة · {item.level||"ابدأ أثرك"}</span>{item.code&&<a href={"/register?role=student&ambassador="+encodeURIComponent(item.code)} target="_blank" rel="noreferrer" className="mt-1 block text-xs font-bold text-teal-700 hover:underline">فتح رابط الإحالة</a>}</div><button onClick={()=>remove(item.id)} className="grid h-10 w-10 place-items-center rounded-lg text-rose-700 hover:bg-rose-50" aria-label={`حذف ${item.name}`}><Trash2 className="h-4 w-4"/></button></article>)}{!items.length&&<div className="rounded-xl bg-slate-50 py-8 text-center text-sm text-slate-500 sm:col-span-2">لا يوجد سفراء مضافون بعد.</div>}</div>
  </section>;
}

function Field({ label, value, onChange, testid, required = false }) {
  return <label className="text-sm font-medium text-slate-700">
    <span>{label}{required ? " *" : ""}</span>
    <input
      value={value || ""}
      onChange={(event) => onChange(event.target.value)}
      required={required}
      className="mt-2 min-h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-base outline-none focus:border-teal-600 focus:ring-2 focus:ring-teal-600/20"
      data-testid={testid}
    />
  </label>;
}
